<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { isDesktopOrWidescreen } from '../../utils/breakpoints';
import Icon from '../Icon/Icon.vue';
import Notifications from '../Notifications/Notifications.vue';
import CustomerNavigationPopper, {
  type EmitCredentialsType,
} from './components/CustomerNavigationPopper/CustomerNavigationPopper.vue';

import logoutWithUrl from '../../xhr/login/logoutWithUrl';
import checkLoginWithUrl from '../../xhr/login/checkLoginWithUrl';
import * as Storage from '../../shared/Storage';
import StorageKeys from '../../globals/constants/storageKeys';
import {
  default as ShopPostMessages,
  ShopQueryActionType,
} from '../../globals/constants/shopPostMessages';
import LoginWithUrl from '../../xhr/login/loginWithUrl';

import type { StoreCustomerConfig } from '../../types/storeConfig';
import type { WindowWika } from '../../types/window';
import type { NavigationNode } from '../../types/link';
import { useStoreTranslations } from '../../store/translations/translations';

interface Props {
  config: StoreCustomerConfig;
}

export type LoginFormType = Pick<
  StoreCustomerConfig,
  'iFramePage' | 'forgotPasswordAction' | 'registrationAction' | 'isSSOavailable' | 'ssoUrl'
>;
export type AccountNavigationItems = Pick<NavigationNode, 'id' | 'label' | 'url' | 'action'>;

const props = defineProps<Props>();
const emit = defineEmits(['userLoggedIn']);

const storeTranslations = useStoreTranslations();

const isUserLoggedIn = ref<boolean>(false);
const isCustomerNavOpen = ref<boolean>(false);
const isLoading = ref<boolean>(true);
const interval = ref<ReturnType<typeof setTimeout> | null>(null);
const shouldShowMessage = ref<boolean>(false);
const messageType = ref<'error' | 'success'>();
const loginForm = ref<LoginFormType>();
const translations = computed(() => storeTranslations.translations);
const currentUrl = ref<string | null>(null);
const stepUrl = ref<string | null>(null);
const loginHrefUrl = ref<string | null>(null);

const toggleNavClick = () => {
  return (isCustomerNavOpen.value = !isCustomerNavOpen.value);
};

const _window = window as unknown as WindowWika;

const checkLogin = (date) => {
  let checkLoginUrl = date
    ? props.config.checkLoginUrl + '?_=' + Date.now()
    : props.config.checkLoginUrl;
  checkLoginWithUrl(checkLoginUrl)
    .then((responseData) => {
      loginInUser(responseData);
    })
    .catch((error) => {
      console.error(JSON.parse(JSON.stringify(error)));
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const login = (loginCredentials: EmitCredentialsType) => {
  shouldShowMessage.value = false;
  if (!loginCredentials) {
    return;
  }

  _window.logInLoading = true;
  window.dispatchEvent(new CustomEvent('logInLoading'));

  LoginWithUrl(props.config.loginUrl, {
    username: loginCredentials.email,
    password: loginCredentials.password,
    _t: Date.now(),
  })
    .then((responseData) => {
      if (typeof responseData === 'string') {
        setLoggedIn(JSON.parse(responseData));
      } else {
        setLoggedIn(responseData);
      }
    })
    .catch((error) => {
      console.error('logIn error', error);
      shouldShowMessage.value = true;
      isUserLoggedIn.value = false;
      messageType.value = 'error';
    });
};

const setLoggedIn = (data) => {
  if (data.status === 1) {
    isUserLoggedIn.value = true;
    if (props.config.historyAction && props.config.iFramePage) {
      window.location.href = props.config.iFramePage + props.config.historyAction;
    }
  } else {
    isUserLoggedIn.value = false;
    shouldShowMessage.value = true;
    messageType.value = 'error';
  }
};

const logout = () => {
  if (props.config.isSSOavailable) {
    // optional cleanup vorher
    Storage.removeSessionStorage(StorageKeys.LOGINDISCOUNT);
    isUserLoggedIn.value = false;
    window.location.href = props.config.logoutUrl;
    return;
  } else {
    logoutWithUrl(props.config.logoutUrl)
      .then((responseData) => {
        if (typeof responseData === 'string') {
          setLoggedOut(JSON.parse(responseData));
        } else {
          setLoggedOut(responseData);
        }
      })
      .catch((error) => console.error(error));
  }
};

const setLoggedOut = (data) => {
  if (data.status === 1) {
    if (interval.value) {
      clearInterval(interval.value);
      interval.value = null;
    }
    isUserLoggedIn.value = false;
    Storage.removeSessionStorage(StorageKeys.LOGINDISCOUNT);
    // TODO: in old component props.config.homepage
    window.location.href = props.config.startPage || '/';
  } else {
    isUserLoggedIn.value = true;
  }
};

const loginInUser = (data) => {
  const logInDiscountFromStorage = Storage.getSessionStorage(StorageKeys.LOGINDISCOUNT);

  if (data.isLoggedIn === true) {
    isUserLoggedIn.value = true;
    (window as unknown as WindowWika).configuratorLoggedIn = true;

    if (data.discount && data.typesExcludedDiscount) {
      (window as unknown as WindowWika).logIn = {
        discount: data.discount,
        typesExcludedDiscount: data.typesExcludedDiscount,
      };
    }

    (window as unknown as WindowWika).logInLoading = false;
    window.dispatchEvent(new CustomEvent('logInLoading'));
    window.dispatchEvent(new CustomEvent('logIn'));

    if (
      !logInDiscountFromStorage ||
      logInDiscountFromStorage.discount !== data.discount ||
      logInDiscountFromStorage.typesExcludedDiscount !== data.typesExcludedDiscount
    ) {
      Storage.setSessionStorage(
        StorageKeys.LOGINDISCOUNT,
        {
          discount: data.discount,
          typesExcludedDiscount: data.typesExcludedDiscount,
        },
        true,
      );
    }

    if (!interval.value) {
      interval.value = setInterval(() => {
        checkLogin(null);
      }, 30000);
    }
  } else {
    if (isUserLoggedIn.value) {
      (window as unknown as WindowWika).configuratorLoggedIn = false;
      logout();
    }
  }
};

const receiveMessage = (event) => {
  const shopBaseUrl = new URL(props.config?.shopBaseUrl || location.origin);
  if (event.origin === shopBaseUrl.origin && event.data === ShopPostMessages.CHECK_LOGIN_STATE) {
    const timeoutForCheckLogin = setTimeout(() => {
      checkLogin(null);
      clearTimeout(timeoutForCheckLogin);
    }, 2000);
  }
  if (
    event.origin === shopBaseUrl.origin &&
    'data' in event &&
    typeof event.data === 'string' &&
    event.data.toUpperCase().includes(ShopPostMessages.ACTION_STEP)
  ) {
    const stepID = event.data.replace(ShopPostMessages.ACTION_STEP + '_', '');
    if (stepID) {
      stepUrl.value = stepID;
      loginHrefUrl.value = loginHref();
    }
  }
};

const generateLoginForm = (fields) => {
  loginForm.value = {
    iFramePage: fields.iFramePage,
    registrationAction: fields.registrationAction,
    forgotPasswordAction: fields.forgotPasswordAction,
    isSSOavailable: fields.isSSOavailable,
    ssoUrl: fields.ssoUrl,
  };
};

const accountNavigation = computed<AccountNavigationItems[]>(() => {
  const t = translations.value;
  const items = props.config;
  if (!items) return [];
  return [
    {
      id: '0',
      label: t?.['customerNavigation.customerNavigationLabels.account'] || 'Account',
      url: items.iFramePage + items.accountAction,
    },
    {
      id: '1',
      label: t?.['customerNavigation.customerNavigationLabels.editAccount'] || 'Edit Account',
      url: items.iFramePage + items.accountEditAction,
    },
    {
      id: '3',
      label: t?.['customerNavigation.customerNavigationLabels.address'] || 'Address',
      url: items.iFramePage + items.addressAction,
    },
    {
      id: '4',
      label: t?.['customerNavigation.customerNavigationLabels.history'] || 'History',
      url: items.iFramePage + items.historyAction,
    },
    {
      id: '5',
      label: t?.['customerNavigation.customerNavigationLabels.logout'] || 'Logout',
      action: logout,
    },
  ];
});

const accountNavigationSSO = computed(() =>
  accountNavigation.value.filter((item) => item.id !== '1'),
);

const loginHref = () => {
  if (!props.config.isSSOavailable) {
    return null;
  }

  const baseUrl = props.config.ssoUrl;

  currentUrl.value = window.location.href;
  //The & and ? values needs to be encoded to be correctly transformed via GET parmeter of referer
  const glueCurrentUrl = currentUrl.value.includes('?') ? '&' : '?';

  if (stepUrl.value) {
    //Check if actionStep already existing in the url query
    const urlObj = new URL(window.location.href);
    if (urlObj.searchParams.has(ShopQueryActionType.ACTION_STEP)) {
      urlObj.searchParams.set(ShopQueryActionType.ACTION_STEP, stepUrl.value);
      currentUrl.value = urlObj.toString();
    } else {
      currentUrl.value += glueCurrentUrl + ShopQueryActionType.ACTION_STEP + '=' + stepUrl.value;
    }
  }
  currentUrl.value = encodeURIComponent(currentUrl.value);
  const glue = baseUrl ? (baseUrl.includes('?') ? '&' : '?') : null;
  return `${baseUrl}${glue}referer=${currentUrl.value}`;
};

watch(
  () => props.config,
  (newValue, oldValue) => {
    if (newValue !== oldValue && newValue) {
      generateLoginForm(newValue);
    }
  },
);

watch(isUserLoggedIn, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    emit('userLoggedIn', newValue);
  }
});

onMounted(() => {
  window.addEventListener('message', receiveMessage, false);
  window.addEventListener('logIn', () => login, false);
  checkLogin(true);
  loginHrefUrl.value = loginHref();

  if (props.config) {
    generateLoginForm(props.config);
  }
});

onUnmounted(() => {
  window.removeEventListener('message', receiveMessage, false);
  window.removeEventListener('logIn', () => login, false);
  if (interval.value) {
    clearInterval(interval.value);
  }
});
</script>

<template>
  <div :class="$style.container">
    <a
      v-if="config.isSSOavailable && loginHrefUrl && !isUserLoggedIn"
      :class="$style.customerLink"
      :href="loginHrefUrl"
    >
      <Icon name="person" size="large" />
      <template v-if="!isDesktopOrWidescreen">
        <span class="accessible-link-text">
          {{
            translations
              ? translations['customerNavigation.customerNavigationLabels.loginAndRegister']
              : 'Login / Register'
          }}
        </span>
      </template>
      <template v-else>
        {{
          translations
            ? translations['customerNavigation.customerNavigationLabels.loginAndRegister']
            : 'Login / Register'
        }}
      </template>
    </a>
    <button
      v-if="
        (config.isSSOavailable && isUserLoggedIn) ||
        (!config.isSSOavailable && isUserLoggedIn) ||
        (!config.isSSOavailable && !isUserLoggedIn)
      "
      :aria-label="
        translations
          ? translations['customerNavigation.customerButton.title']
          : 'customer navigation trigger'
      "
      :class="$style.customerButton"
      @click="toggleNavClick"
    >
      <Icon name="person" size="large" />
      <span v-if="isUserLoggedIn" class="accessible-link-text">
        {{ translations ? translations['customerNavigation.customerButton.label'] : 'Profile' }}
      </span>
      <span v-else class="accessible-link-text">
        {{
          translations ? translations['customerNavigation.customerButton.active.label'] : 'Profile'
        }}</span
      >
    </button>
    <CustomerNavigationPopper
      v-if="accountNavigation || accountNavigationSSO || loginForm"
      :is-open="isCustomerNavOpen"
      :items="config.isSSOavailable ? accountNavigationSSO : accountNavigation"
      :logged-in-user="isUserLoggedIn"
      :login-form="loginForm"
      @close="toggleNavClick"
      @login="login"
    />
    <Notifications
      v-if="translations ? translations['customerNavigation.login.errorMsg'] : 'There was an error'"
      :message="
        translations ? translations['customerNavigation.login.errorMsg'] : 'There was an error'
      "
      :open="shouldShowMessage"
      :pattern="messageType === 'error' ? 'persistent' : 'inline'"
      :type="messageType"
      @update:open="shouldShowMessage = $event"
    />
  </div>
</template>

<style module lang="scss">
@forward './CustomerNavigation1XP.scss';
</style>
